import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, LineChart, Wallet, FileBarChart, UserPlus, Banknote, PieChart, ArrowDownToLine } from "lucide-react";
import { api, money, pct } from "@/lib/api";
import Ticker from "@/components/site/Ticker";
import StockCard from "@/components/site/StockCard";
import PropertyCard from "@/components/site/PropertyCard";
import YieldCalculator from "@/components/site/YieldCalculator";
import EnquiryForm from "@/components/site/EnquiryForm";
import Testimonials from "@/components/site/Testimonials";

const HERO_IMG = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICES = [
  { icon: LineChart, title: "Listed equities", body: "Buy and sell shares in the companies we list, with prices published transparently on our markets page." },
  { icon: Building2, title: "Fractional real estate", body: "Own shares in income-producing properties from a single share upward, with the rental yield shown up front." },
  { icon: Wallet, title: "Managed funding", body: "Deposit and withdrawal requests are reviewed by our team and credited to your cash balance on approval." },
  { icon: FileBarChart, title: "Clear reporting", body: "One dashboard for cash, holdings, gains and every transaction — always up to date." },
];

const STEPS = [
  { icon: UserPlus, title: "Open your account", body: "Register in under a minute with your email." },
  { icon: Banknote, title: "Request a deposit", body: "Submit a deposit; once approved, funds appear in your balance." },
  { icon: PieChart, title: "Build your portfolio", body: "Invest across stocks and property shares." },
  { icon: ArrowDownToLine, title: "Withdraw when ready", body: "Request a withdrawal of available cash at any time." },
];

function Hero({ stock, property }) {
  return (
    <section className="grain relative overflow-hidden">
      <div className="grid-lines absolute inset-0" />
      <div className="container-x relative z-10 grid items-center gap-14 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-6">
          <p className="eyebrow fade-up">Stocks · Real estate · One ledger</p>
          <h1 className="fade-up delay-1 mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            Own the market.<br />
            <span className="text-emerald-400">Own</span> the <span className="text-amber-500">skyline</span>.
          </h1>
          <p className="fade-up delay-2 mt-8 max-w-lg text-base leading-relaxed text-slate-400">
            Meridian Stock Exchange lets you build a portfolio of listed stocks and fractional property shares — fund your account, invest, and track every position from one dashboard.
          </p>
          <div className="fade-up delay-3 mt-10 flex flex-wrap gap-4">
            <Link to="/register" className="btn-primary" data-testid="hero-open-account-button">Open an account <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/markets" className="btn-ghost" data-testid="hero-explore-markets-link">Explore markets</Link>
          </div>
        </div>
        <div className="fade-up delay-2 relative lg:col-span-6">
          <div className="overflow-hidden rounded-3xl border border-line">
            <img src={HERO_IMG} alt="Modern villa at dusk" className="aspect-[4/3] w-full object-cover" />
          </div>
          {stock && (
            <div className="absolute -left-4 top-8 rounded-2xl border border-white/10 bg-obsidian/70 p-5 backdrop-blur-xl sm:-left-10" data-testid="hero-stock-badge">
              <p className="eyebrow">{stock.ticker}</p>
              <p className="mt-2 font-mono text-xl tracking-tight text-white">{money(stock.price)}</p>
              <p className={`font-mono text-sm ${stock.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(stock.change_pct)}</p>
            </div>
          )}
          {property && (
            <div className="absolute -bottom-6 right-4 rounded-2xl border border-white/10 bg-obsidian/70 p-5 backdrop-blur-xl sm:right-8" data-testid="hero-property-badge">
              <p className="eyebrow">{property.title}</p>
              <p className="mt-2 font-mono text-xl tracking-tight text-amber-500">{property.rental_yield}% yield</p>
              <p className="font-mono text-sm text-slate-400">{money(property.share_price, 0)} / share</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const SectionHead = ({ eyebrow, title, link, linkLabel, testId }) => (
  <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">{title}</h2>
    </div>
    {link && <Link to={link} className="-my-2 inline-flex items-center gap-2 py-2 text-sm text-slate-300 hover:text-emerald-400" data-testid={testId}>{linkLabel} <ArrowRight className="h-4 w-4" /></Link>}
  </div>
);

const IconList = ({ items, cols }) => (
  <div className={`grid gap-6 sm:grid-cols-2 ${cols}`}>
    {items.map(({ icon: Icon, title, body }, i) => (
      <div key={title} className="panel lift p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Icon className="h-6 w-6 text-emerald-400" strokeWidth={1.5} />
          <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
        </div>
        <h3 className="mt-8 font-display text-lg font-semibold text-white">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{body}</p>
      </div>
    ))}
  </div>
);

export default function Home() {
  const [stocks, setStocks] = useState([]);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    api.get("/stocks").then(({ data }) => setStocks(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
  }, []);

  const pick = (list) => {
    const featured = list.filter((x) => x.featured);
    return (featured.length ? featured : list).slice(0, 3);
  };
  const topStocks = pick(stocks);
  const topProps = pick(properties);

  return (
    <div data-testid="home-page">
      <Hero stock={topStocks[0]} property={topProps[0]} />
      <Ticker stocks={stocks} properties={properties} />

      <section className="container-x py-24">
        <SectionHead eyebrow="Featured equities" title="Stocks on our board" link="/markets" linkLabel="All stocks" testId="home-all-stocks-link" />
        <div className="grid gap-6 md:grid-cols-3">{topStocks.map((s) => <StockCard key={s.id} stock={s} />)}</div>
      </section>

      <section className="container-x pb-24">
        <SectionHead eyebrow="Featured real estate" title="Property you can own a share of" link="/properties" linkLabel="All properties" testId="home-all-properties-link" />
        <div className="grid gap-6 md:grid-cols-3">{topProps.map((p) => <PropertyCard key={p.id} property={p} />)}</div>
      </section>

      <Testimonials />

      <section className="border-y border-slate-800 bg-surface/40 py-24">
        <div className="container-x">
          <SectionHead eyebrow="What we offer" title="Services" />
          <IconList items={SERVICES} cols="lg:grid-cols-4" />
        </div>
      </section>

      <section className="container-x py-24">
        <SectionHead eyebrow="How it works" title="From sign-up to first investment" />
        <IconList items={STEPS} cols="lg:grid-cols-4" />
      </section>

      <section className="container-x pb-24">
        <SectionHead eyebrow="Plan ahead" title="Growth calculator" />
        <YieldCalculator />
      </section>

      <section className="container-x grid gap-12 pb-28 lg:grid-cols-12" id="enquire">
        <div className="lg:col-span-5">
          <p className="eyebrow">Talk to us</p>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Have a question before you invest?</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">Send us an enquiry about any stock, property or your account and our team will reply by email.</p>
        </div>
        <div className="lg:col-span-7"><EnquiryForm /></div>
      </section>
    </div>
  );
}
